import { SuitInfo } from "components";
import { InputField } from "components/inputs";
import { localePrice } from "helpers/locales";
import { ReactComponent as IconSpinner } from "icons/icon-spinner.svg";
import { useEffect } from "react";
import { FaGift } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";

import {
  useAddPromoCodeMutation,
  useDeletePromoCodeMutation,
  useGetReservationMutation,
  useUpdatePaymentMutation,
} from "app/eventsApi";
import { setMessage } from "features/appSlice";
import { tryRedirectReservationClosedInvoices } from "helpers/reservationClosedRedirect";
import { selectReservationId } from "features/reservationSlice";
import { selectUser } from "features/userSlice";
import { useDispatch, useSelector } from "react-redux";

const EventGroup = ({ event }) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const [updatePayment] = useUpdatePaymentMutation();
  const [addPromoCode, { isLoading: isSubmitting }] = useAddPromoCodeMutation();
  const [deletePromoCode, { isLoading: isDeleting }] =
    useDeletePromoCodeMutation();
  const [getReservation] = useGetReservationMutation();
  const user = useSelector(selectUser);
  const reservation_uuid = useSelector(selectReservationId);

  // const isPrepay = event.price.find((el) => el.is_selected).prepay;

  const schema = Yup.object().shape({
    promoCode: Yup.string().required("Поле не может быть пустым"),
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), reValidateMode: undefined });

  const isValid = watch("promoCode") ? true : false;

  useEffect(() => {
    if (errors.promoCode) {
      setTimeout(() => {
        clearErrors();
      }, 3000);
    }
  }, [errors.promoCode, clearErrors]);

  const onSubmit = async (data) => {
    try {
      await addPromoCode({
        promo_code: data.promoCode,
        event_id: event.event_id,
        user_reservation_uuid: event.user_reservation_uuid,
      }).unwrap();
      await getReservation({
        user_id: user.id,
      }).unwrap();
      dispatch(
        setMessage({ type: "success", msg: "Промокод был успешно применен" })
      );
      reset();
    } catch (err) {
      if (tryRedirectReservationClosedInvoices(navigate, reservation_uuid, err)) {
        return;
      }
      setError("promoCode", {
        type: "manual",
        message: err.data?.error || "Неверный промокод",
      });
    }
  };

  const handleDeletePromo = async () => {
    try {
      await deletePromoCode({
        promo_code: event.promo_code.name,
        event_id: event.event_id,
        user_reservation_uuid: event.user_reservation_uuid,
      }).unwrap();
      await getReservation({
        user_id: user.id,
      }).unwrap();
    } catch (err) {
      tryRedirectReservationClosedInvoices(navigate, reservation_uuid, err);
    }
  };

  const handleSelect = async (paymentOption) => {
    try {
      await updatePayment({
        user_id: user.id,
        reservation_uuid,
        event_id: event.event_id,
        price: {
          ...paymentOption,
          is_selected: true,
        },
      }).unwrap();
      await getReservation({
        user_id: user.id,
      }).unwrap();
    } catch (err) {
      if (tryRedirectReservationClosedInvoices(navigate, reservation_uuid, err)) {
        return;
      }
      dispatch(
        setMessage({
          type: "error",
          msg: "Бронирование не найдено",
        })
      );
      navigate("/reservation", { replace: true });
    }
  };

  return (
    <div className="checkout-list-group">
      <article
        className="card card-container"
        aria-label="Информация о бронировании"
      >
        <h5 className="card-event-name">{event.header}</h5>
        {event.suits &&
          event.suits.map((item) => {
            return (
              <SuitInfo
                key={item.id}
                event={event}
                suit={item}
                editable={true}
              />
            );
          })}
        <div className="checkout-payment">
          <h5>Варианты оплаты</h5>
          <div className="payment-select">
            {event.price.map((el) => {
              const isCash =
                el.payment_type === 1 || /наличн/i.test(el.name ?? "");
              return (
                <div key={el.name} className="payment-option">
                  <button
                    className={
                      el.is_selected
                        ? "btn btn-payment"
                        : "btn btn-payment active"
                    }
                    disabled={el.is_selected}
                    onClick={() => handleSelect(el)}
                  >
                    <span className="payment-radio"></span>
                    <span className="payment-label">
                      {el.name} {el.prepay_amount && `${el.prepay_amount}%`}
                    </span>
                    <div className="payment-price-container">
                      <span className="payment-price">
                        {el.discount && !el.prepay
                          ? localePrice(el.discount.price)
                          : localePrice(el.price)}
                      </span>
                      {el.discount && !el.prepay && el.discount.amount !== el.discount.price && (
                        <span className="full-price">
                          {localePrice(el.discount.amount)}
                        </span>
                      )}
                    </div>
                  </button>
                  {isCash && el.is_selected && (
                    <div className="cash-payment-message">
                      <small>Свяжитесь с менеджером для оплаты наличными</small>
                      <br />
                      <small>
                        Оплата принимается в Space Химки. г. Химки, ул. Кирова, с24
                      </small>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <form className="checkout-promo" onSubmit={handleSubmit(onSubmit)}>
          <h5>Промокод</h5>
          {event.promo_code ? (
            <div className="checkout-promo-success">
              <div className="checkout-promo-success-text">
                <FaGift />
                <span>
                  {event.promo_code.name}: скидка {event.promo_code.discount}%
                </span>
              </div>
              <button
                type="button"
                className="btn-upper"
                onClick={handleDeletePromo}
                data-delete
              >
                {isDeleting ? <IconSpinner className="spinner" /> : "Удалить"}
              </button>
            </div>
          ) : (
            <InputField
              autoComplete="off"
              placeholder="Введите промокод"
              {...register("promoCode")}
              error={errors.promoCode?.message}
            >
              <button
                type="submit"
                className="btn-upper"
                disabled={!isValid || undefined}
              >
                {isSubmitting ? (
                  <IconSpinner className="spinner" />
                ) : (
                  "Добавить"
                )}
              </button>
            </InputField>
          )}
        </form>
      </article>
    </div>
  );
};

export default EventGroup;
